import type {
  StoredChatMessage,
  StoredTombstone,
  MessageCursor,
  MessagePage,
  ListMessagesInput,
} from './chat-contracts.js';
import { VaultDB, globalVaultDB } from '../../journal/vault-db.js';
import { globalInvalidationBus, VaultInvalidationBus } from '../../journal/vault-invalidation-bus.js';

/**
 * src/shell/chat/MessageRepository.ts
 * 
 * Domain repository governing message persistence, compound cursor queries,
 * revision conflict management, and tombstone tracking against VaultDB.
 */

export class MessageRepository {
  constructor(
    private readonly vaultDb: VaultDB = globalVaultDB,
    private readonly invalidationBus: VaultInvalidationBus = globalInvalidationBus
  ) {}

  async listAfter(input: ListMessagesInput): Promise<MessagePage> {
    return this.vaultDb.listChatMessagesAfter(input.roomId, input.cursor, input.limit ?? 50);
  }

  async listNewer(roomId: string, sinceTimestamp: number): Promise<StoredChatMessage[]> {
    return this.vaultDb.listChatMessagesNewer(roomId, sinceTimestamp);
  }

  async getAllForRoom(roomId: string): Promise<StoredChatMessage[]> {
    return this.vaultDb.getAllMessagesForRoom(roomId);
  }

  async getById(eventId: string): Promise<StoredChatMessage | undefined> {
    return this.vaultDb.getChatMessage(eventId);
  }

  async save(message: StoredChatMessage, source: 'local' | 'mesh' | 'restore' = 'local'): Promise<void> {
    // 1. Guard against tombstoned messages
    if (await this.vaultDb.isTombstoned(message.eventId)) {
      return;
    }

    // 2. Check existing record
    const existing = await this.vaultDb.getChatMessage(message.eventId);
    if (existing) {
      // Idempotent duplicate: identical eventId and payloadHash
      if (existing.payloadHash === message.payloadHash && existing.revision === message.revision) {
        return;
      }

      // Conflict / Tampering check: same eventId but different payloadHash on same revision
      if (existing.revision === message.revision && existing.payloadHash !== message.payloadHash) {
        console.warn(`[MessageRepository] Integrity violation: conflicting payload hash on event ${message.eventId}`);
        return;
      }

      // Revision check: reject older revision
      if (existing.revision && message.revision && existing.revision >= message.revision) {
        return;
      }
    }

    // 3. Persist to VaultDB
    await this.vaultDb.saveChatMessage(message);

    // 4. Publish invalidation event
    this.invalidationBus.publish({
      type: existing ? 'message-updated' : 'message-added',
      meshId: message.meshId,
      workspaceId: message.workspaceId,
      roomId: message.roomId,
      eventId: message.eventId,
      source,
      timestamp: Date.now(),
    });
  }

  async applyTombstone(tombstone: StoredTombstone, source: 'local' | 'mesh' | 'restore' = 'local'): Promise<void> {
    await this.vaultDb.saveTombstone(tombstone);

    this.invalidationBus.publish({
      type: 'message-deleted',
      meshId: 'mesh-sovereign-alpha',
      workspaceId: tombstone.workspaceId,
      roomId: tombstone.targetRoomId,
      eventId: tombstone.targetEventId,
      source,
      timestamp: Date.now(),
    });
  }

  async clearLocalRoom(roomId: string, workspaceId: string = 'ws-default'): Promise<void> {
    await this.vaultDb.clearRoomLocal(roomId);

    this.invalidationBus.publish({
      type: 'room-cleared',
      meshId: 'mesh-sovereign-alpha',
      workspaceId,
      roomId,
      source: 'local',
      timestamp: Date.now(),
    });
  }
}

export const globalMessageRepository = new MessageRepository();
